import { Button } from "@aurumiq/ui/components/button";
import { type ColumnDef, createColumnHelper, DataTable } from "@aurumiq/ui/composite/data-table";
import { ReasonDialog } from "@aurumiq/ui/composite/reason-dialog";
import { StatusTag } from "@aurumiq/ui/composite/status-tag";
import { dmy, relativeTime } from "@aurumiq/ui/lib/format";
import { ExternalLink, MonitorSmartphone, Trash2, UserPlus } from "lucide-react";
import { useCallback, useMemo, useState } from "react";
import { useMe, usePermission } from "@/auth/use-permission";
import { NativeSelect } from "@/components/field";
import { errorMessage } from "@/lib/api";
import type { MemberRole, OrgMember } from "@/lib/types";
import { InviteDialog, MEMBER_ROLES } from "./invite-dialog";
import {
  openAsMember,
  useImpersonateMember,
  useOrgMembers,
  usePatchMember,
  useRemoveMember,
} from "./queries";

const col = createColumnHelper<OrgMember>();

export function UsersTab({ orgId }: { orgId: string }) {
  const canWrite = usePermission("orgs:write");
  const members = useOrgMembers(orgId);
  const patch = usePatchMember(orgId);
  const remove = useRemoveMember(orgId);
  const impersonate = useImpersonateMember(orgId);
  const isSuperadmin = useMe().data?.role === "superadmin";
  const [inviteOpen, setInviteOpen] = useState(false);
  const openPortalAs = useCallback(
    (m: OrgMember) => openAsMember(impersonate, m.id, "portal"),
    [impersonate],
  );
  const openWorkstationAs = useCallback(
    (m: OrgMember) => openAsMember(impersonate, m.id, "workstation"),
    [impersonate],
  );
  const [removing, setRemoving] = useState<OrgMember | null>(null);

  const columns = useMemo(
    () => [
      col.accessor("email", {
        header: "Email",
        cell: (c) => <span className="font-semibold">{c.getValue()}</span>,
      }),
      col.accessor("role", {
        header: "Role",
        cell: (c) =>
          canWrite ? (
            <NativeSelect
              aria-label={`Role for ${c.row.original.email}`}
              className="h-7 w-28 text-xs"
              value={c.getValue()}
              disabled={patch.isPending}
              onChange={(e) =>
                patch.mutate({ mid: c.row.original.id, role: e.target.value as MemberRole })
              }
            >
              {MEMBER_ROLES.map((r) => (
                <option key={r} value={r}>
                  {r}
                </option>
              ))}
            </NativeSelect>
          ) : (
            c.getValue()
          ),
      }),
      col.accessor("invited_at", { header: "Invited", cell: (c) => dmy(c.getValue()) || "—" }),
      col.accessor("accepted_at", {
        header: "Accepted",
        cell: (c) =>
          c.getValue() ? dmy(c.getValue()) : <StatusTag variant="warn">PENDING</StatusTag>,
      }),
      col.accessor("last_sign_in_at", {
        header: "Last sign-in",
        cell: (c) => <span title={c.getValue() ?? undefined}>{relativeTime(c.getValue())}</span>,
      }),
      col.display({
        id: "actions",
        header: "",
        cell: (c) => (
          <span className="inline-flex gap-1">
            {isSuperadmin ? (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => openPortalAs(c.row.original)}
                disabled={impersonate.isPending}
                title="Open the customer portal signed in as this member (audited)"
                aria-label={`Sign in to the portal as ${c.row.original.email}`}
              >
                <ExternalLink /> Portal
              </Button>
            ) : null}
            {isSuperadmin ? (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => openWorkstationAs(c.row.original)}
                disabled={impersonate.isPending}
                title="Open the cloud workstation signed in as this member (audited)"
                aria-label={`Open the workstation as ${c.row.original.email}`}
              >
                <MonitorSmartphone /> Workstation
              </Button>
            ) : null}
            {canWrite ? (
              <Button
                variant="ghost"
                size="sm"
                className="text-danger"
                onClick={() => setRemoving(c.row.original)}
                aria-label={`Remove ${c.row.original.email}`}
              >
                <Trash2 /> Remove
              </Button>
            ) : null}
          </span>
        ),
        meta: { numeric: true },
      }),
    ],
    [canWrite, patch, isSuperadmin, impersonate.isPending, openPortalAs, openWorkstationAs],
  );

  return (
    <>
      <DataTable
        columns={columns as ColumnDef<OrgMember, unknown>[]}
        data={members.data?.items ?? []}
        isLoading={members.isPending}
        getRowId={(m) => m.id}
        emptyMessage={
          members.isError
            ? `Could not load members: ${errorMessage(members.error)}`
            : "No members yet."
        }
        toolbar={
          <>
            <span className="text-muted-foreground text-sm">
              {members.data
                ? `${members.data.total} member${members.data.total === 1 ? "" : "s"}`
                : ""}
            </span>
            {canWrite ? (
              <Button variant="gold" className="ml-auto" onClick={() => setInviteOpen(true)}>
                <UserPlus /> Invite user
              </Button>
            ) : null}
          </>
        }
      />
      <InviteDialog orgId={orgId} open={inviteOpen} onOpenChange={setInviteOpen} />
      <ReasonDialog
        open={removing !== null}
        onOpenChange={(o) => {
          if (!o) setRemoving(null);
        }}
        title={`Remove ${removing?.email}?`}
        description="They lose access to this organisation immediately. Their invoices and data stay."
        confirmLabel="Remove member"
        onConfirm={async (reason) => {
          if (removing) await remove.mutateAsync({ mid: removing.id, reason });
        }}
      />
    </>
  );
}
